import { createElement, useState } from "react";
import { trackEvent } from "../utils/analytics";
import { MEDIA_KIT_ASSETS } from "../data/mediaKitAssets";

type Status = "idle" | "generating" | "error";

/**
 * ZP-49's "Download Media Kit" ZIP — same idle/generating/error shape as
 * usePdfDownload.ts, but not a literal reuse: this bundles multiple real
 * files (whatever's listed in mediaKitAssets.ts, today just logo.png) plus
 * a generated Brand Usage Terms PDF into one archive, rather than
 * generating a single PDF document.
 */
export default function useMediaKitDownload() {
  const [status, setStatus] = useState<Status>("idle");

  const download = async () => {
    setStatus("generating");
    try {
      const [{ default: JSZip }, { pdf }, { default: BrandUsageTermsPdf }] = await Promise.all([
        import("jszip"),
        import("@react-pdf/renderer"),
        import("../components/BrandUsageTermsPdf"),
      ]);

      const zip = new JSZip();

      await Promise.all(
        MEDIA_KIT_ASSETS.map(async (asset) => {
          const res = await fetch(asset.url);
          // fetch() only rejects on a network-level failure — a 404/500
          // response still resolves normally, so without this check a bad
          // asset.url would silently zip up an error page's HTML as if it
          // were the real file instead of surfacing status "error".
          if (!res.ok) throw new Error(`Failed to fetch ${asset.url}: ${res.status}`);
          const blob = await res.blob();
          zip.file(asset.filename, blob);
        })
      );

      const termsBlob = await pdf(createElement(BrandUsageTermsPdf)).toBlob();
      zip.file("Brand-Usage-Terms.pdf", termsBlob);

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "ZenFresh-Media-Kit.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      trackEvent("press_kit_download");
      setStatus("idle");
    } catch {
      setStatus("error");
    }
  };

  return { status, download };
}
